import React, { useCallback } from "react";
import * as Highcharts from "highcharts";
import Boost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";

import {
  fetchAthenaResults,
  AthenaResultsSource,
} from "components/athena/FetchAthenaResults";
import { useAsyncData } from "hooks";

import { HighchartsReactWithCallbackFix } from "./HighchartsReactWithCallbackFix";
import { getHighchartOptionsForCsv } from "./HighchartOptionsFromCsv";

NoDataToDisplay(Highcharts);
Boost(Highcharts);
Exporting(Highcharts);

export const HighchartsFromAthenaResults = ({
  athenaResults,
  onStatusUpdate,
  onComplete,
  onLoading,
}: {
  athenaResults: AthenaResultsSource | undefined;
  onStatusUpdate?: (status: string) => void;
  onComplete?: ({
    url,
    totalSizeInKB,
    totalRows,
    error,
  }: {
    url: string;
    totalSizeInKB: number;
    totalRows: number;
    error: unknown;
  }) => void;
  onLoading?: () => void;
}) => {
  const { url, headUrl } = athenaResults || {
    url: undefined,
    headUrl: undefined,
  };
  const loadData = useCallback(async () => {
    if (!url || !headUrl) return;
    let totalSizeInKB = 0;
    onLoading?.();
    const data = await fetchAthenaResults(
      { url, headUrl },
      {
        onStatusUpdate:
          onStatusUpdate ||
          (() => {
            /* */
          }),
        onDownloadComplete: (reportedTotalSizeInKB) =>
          (totalSizeInKB = reportedTotalSizeInKB),
      }
    );
    if (data.error) {
      onComplete?.({
        url,
        totalSizeInKB,
        totalRows: data.parsedRows.length,
        error: data.error,
      });
      return;
    }
    onStatusUpdate?.(`Rendering ${data.parsedRows.length} results...`);
    return {
      ...getHighchartOptionsForCsv(data, () => {
        onStatusUpdate?.("Done");
        onComplete?.({
          url,
          totalSizeInKB,
          totalRows: data.parsedRows.length,
          error: data.error,
        });
      }),
      sourceUrl: url,
    };
  }, [url, headUrl, onLoading, onStatusUpdate, onComplete]);

  const { data: highchartOptions } = useAsyncData(loadData, {
    initialData: undefined,
  });

  return (
    <>
      {highchartOptions && (
        <HighchartsReactWithCallbackFix
          /* used so we re-load highcharts when we're showing data from a different source
             as it's onLoad event only fires once... */
          key={highchartOptions.sourceUrl}
          highcharts={Highcharts}
          options={highchartOptions}
        />
      )}
    </>
  );
};
